import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, DatePicker, Form, Input, message, Modal, Select, Space, Table, Tag, Radio } from 'antd';
import dayjs from 'dayjs';
import api from '../api/http';
import { useAuth } from '../context/AuthContext';

const TYPE_OPTIONS = [
  { label: '通知', value: 'notice' },
  { label: '提醒', value: 'reminder' },
];

const STATUS_OPTIONS = [
  { label: '全部状态', value: '' },
  { label: '草稿', value: 'draft' },
  { label: '定时待发', value: 'scheduled' },
  { label: '已发布', value: 'published' },
  { label: '已取消', value: 'cancelled' },
];

const STATUS_COLOR = {
  draft: 'default',
  scheduled: 'processing',
  published: 'success',
  cancelled: 'error',
};

const CHANNEL_OPTIONS = [
  { label: '站内通知', value: 'inapp' },
  { label: '微信小程序', value: 'wechat_mini_program' },
];

const DEFAULT_FORM_VALUES = {
  type: 'notice',
  title: '',
  summary: '',
  content: '',
  sendMode: 'immediate',
  sendChannels: ['inapp', 'wechat_mini_program'],
  userIds: [],
  roleNames: [],
  departments: [],
  miniProgramPage: '',
  miniProgramQuery: '',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const time = dayjs(value);
  return time.isValid() ? time.format('YYYY-MM-DD HH:mm') : String(value);
};

export default function HRNotificationsPage() {
  const { hasPermission } = useAuth() || { hasPermission: () => false };
  const canManage = hasPermission(['hr_notifications_manage']);
  const canPublish = hasPermission(['hr_notifications_publish']);

  const [queryForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [listLoading, setListLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [deliveryRows, setDeliveryRows] = useState([]);
  const [recipientOptions, setRecipientOptions] = useState({ roles: [], departments: [] });
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [bindStatus, setBindStatus] = useState({ bound: false, openidMasked: '', bindAt: null });

  const sendMode = Form.useWatch('sendMode', editForm);
  const sendChannels = Form.useWatch('sendChannels', editForm) || [];

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const values = queryForm.getFieldsValue();
      const params = {
        page,
        pageSize,
      };
      if (values.keyword) params.keyword = values.keyword;
      if (values.type) params.type = values.type;
      if (values.status) params.status = values.status;
      const { data } = await api.get('/hr-notifications', { params });
      setRecords(data.records || []);
      setTotal(Number(data.total || 0));
    } catch (error) {
      message.error(error?.message || '加载通知失败');
    } finally {
      setListLoading(false);
    }
  }, [page, pageSize, queryForm]);

  const loadRecipientOptions = useCallback(async () => {
    try {
      const { data } = await api.get('/hr-notifications/recipient-options');
      setRecipientOptions({
        roles: data.roles || [],
        departments: data.departments || [],
      });
    } catch (error) {
      message.error(error?.message || '加载接收对象选项失败');
    }
  }, []);

  const loadBindStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/hr-notifications/wechat-mini-program/bind-status');
      setBindStatus({
        bound: Boolean(data.bound),
        openidMasked: data.openidMasked || '',
        bindAt: data.bindAt || null,
      });
    } catch (error) {
      setBindStatus({ bound: false, openidMasked: '', bindAt: null });
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    loadRecipientOptions();
    loadBindStatus();
  }, [loadRecipientOptions, loadBindStatus]);

  const searchUsers = useCallback(async (keyword) => {
    const normalized = String(keyword || '').trim();
    if (!normalized) return;
    setUserSearchLoading(true);
    try {
      const { data } = await api.get('/hr-notifications/recipients/search', {
        params: { q: normalized, limit: 20 },
      });
      setUserOptions((data.records || []).map((item) => ({
        value: item.id,
        label: item.label,
        wx_bound: item.wx_bound,
      })));
    } catch (error) {
      message.error(error?.message || '搜索接收人失败');
    } finally {
      setUserSearchLoading(false);
    }
  }, []);

  const resetEditor = useCallback(() => {
    setEditingRecord(null);
    editForm.resetFields();
    editForm.setFieldsValue(DEFAULT_FORM_VALUES);
  }, [editForm]);

  const openCreate = useCallback(() => {
    resetEditor();
    setModalOpen(true);
  }, [resetEditor]);

  const openEdit = useCallback((record) => {
    setEditingRecord(record);
    const recipientConfig = record.recipient_config || {};
    editForm.setFieldsValue({
      type: record.type || 'notice',
      title: record.title || '',
      summary: record.summary || '',
      content: record.content || '',
      sendMode: record.send_mode || 'immediate',
      scheduledAt: record.scheduled_at ? dayjs(record.scheduled_at) : null,
      sendChannels: record.send_channels || ['inapp', 'wechat_mini_program'],
      userIds: recipientConfig.userIds || [],
      roleNames: recipientConfig.roleNames || [],
      departments: recipientConfig.departments || [],
      miniProgramPage: record.mini_program_page || '',
      miniProgramQuery: record.mini_program_query || '',
    });
    setModalOpen(true);
  }, [editForm]);

  const buildPayload = useCallback((values) => ({
    type: values.type,
    title: values.title,
    summary: values.summary || '',
    content: values.content || '',
    sendMode: values.sendMode,
    scheduledAt: values.scheduledAt ? values.scheduledAt.format('YYYY-MM-DD HH:mm:ss') : null,
    sendChannels: values.sendChannels || [],
    recipientConfig: {
      userIds: values.userIds || [],
      roleNames: values.roleNames || [],
      departments: values.departments || [],
    },
    miniProgramPage: values.miniProgramPage || '',
    miniProgramQuery: values.miniProgramQuery || '',
  }), []);

  const submitEditor = useCallback(async () => {
    try {
      const values = await editForm.validateFields();
      const payload = buildPayload(values);
      setSaving(true);
      if (editingRecord?.id) {
        await api.put(`/hr-notifications/${editingRecord.id}`, payload);
        message.success('通知已更新');
      } else {
        await api.post('/hr-notifications', payload);
        message.success('通知已创建');
      }
      setModalOpen(false);
      resetEditor();
      loadList();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  }, [buildPayload, editForm, editingRecord, loadList, resetEditor]);

  const publishRecord = useCallback((record) => {
    Modal.confirm({
      title: record.send_mode === 'scheduled' ? '确认定时发布？' : '确认立即发布？',
      content: record.send_mode === 'scheduled'
        ? `计划发送时间：${formatDateTime(record.scheduled_at)}`
        : '发布后将立即生成投递记录。',
      onOk: async () => {
        try {
          await api.post(`/hr-notifications/${record.id}/publish`);
          message.success(record.send_mode === 'scheduled' ? '已加入定时发送' : '发布成功');
          loadList();
        } catch (error) {
          message.error(error?.message || '发布失败');
        }
      },
    });
  }, [loadList]);

  const cancelRecord = useCallback((record) => {
    Modal.confirm({
      title: '确认取消该通知？',
      content: '仅未发布或定时待发的通知可以取消。',
      onOk: async () => {
        try {
          await api.post(`/hr-notifications/${record.id}/cancel`);
          message.success('已取消');
          loadList();
        } catch (error) {
          message.error(error?.message || '取消失败');
        }
      },
    });
  }, [loadList]);

  const deleteRecord = useCallback((record) => {
    Modal.confirm({
      title: '确认删除该通知？',
      content: '已发布通知不允许删除。',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.delete(`/hr-notifications/${record.id}`);
          message.success('已删除');
          loadList();
        } catch (error) {
          message.error(error?.message || '删除失败');
        }
      },
    });
  }, [loadList]);

  const openDeliveries = useCallback(async (record) => {
    setDeliveryOpen(true);
    setDeliveryLoading(true);
    try {
      const { data } = await api.get(`/hr-notifications/${record.id}/deliveries`);
      setDeliveryRows(data.records || []);
    } catch (error) {
      message.error(error?.message || '加载投递记录失败');
      setDeliveryRows([]);
    } finally {
      setDeliveryLoading(false);
    }
  }, []);

  const columns = useMemo(() => [
    {
      title: '标题',
      dataIndex: 'title',
      width: 220,
      render: (value, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{value}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{record.summary || '-'}</div>
        </div>
      ),
    },
    {
      title: '类型',
      dataIndex: 'type',
      width: 90,
      render: (value) => value === 'reminder' ? <Tag color='gold'>提醒</Tag> : <Tag color='blue'>通知</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: (value) => <Tag color={STATUS_COLOR[value] || 'default'}>{value || '-'}</Tag>,
    },
    {
      title: '发送方式',
      dataIndex: 'send_mode',
      width: 120,
      render: (value) => value === 'scheduled' ? '定时发送' : '立即发送',
    },
    {
      title: '接收人数',
      dataIndex: 'recipient_count',
      width: 100,
      render: (value) => Number(value || 0),
    },
    {
      title: '计划发送',
      dataIndex: 'scheduled_at',
      width: 160,
      render: (value) => formatDateTime(value),
    },
    {
      title: '渠道',
      dataIndex: 'send_channels',
      width: 180,
      render: (value) => (value || []).map((item) => (
        <Tag key={item}>{item === 'wechat_mini_program' ? '微信小程序' : '站内通知'}</Tag>
      )),
    },
    {
      title: '操作',
      key: 'action',
      width: 320,
      render: (_, record) => (
        <Space wrap>
          {canManage && record.status !== 'published' && <Button size='small' onClick={() => openEdit(record)}>编辑</Button>}
          {canPublish && ['draft', 'cancelled', 'scheduled'].includes(record.status) && <Button size='small' type='primary' onClick={() => publishRecord(record)}>发布</Button>}
          {canPublish && record.status === 'scheduled' && <Button size='small' onClick={() => cancelRecord(record)}>取消</Button>}
          <Button size='small' onClick={() => openDeliveries(record)}>投递记录</Button>
          {canManage && record.status !== 'published' && <Button size='small' danger onClick={() => deleteRecord(record)}>删除</Button>}
        </Space>
      ),
    },
  ], [canManage, canPublish, cancelRecord, deleteRecord, openDeliveries, openEdit, publishRecord]);

  const deliveryColumns = [
    { title: '接收人', dataIndex: 'user_name', width: 140 },
    { title: '部门', dataIndex: 'department', width: 120, render: (value) => value || '-' },
    { title: '渠道', dataIndex: 'channel', width: 120, render: (value) => value === 'wechat_mini_program' ? '微信小程序' : '站内通知' },
    { title: '状态', dataIndex: 'status', width: 100, render: (value) => <Tag color={value === 'sent' ? 'success' : value === 'failed' ? 'error' : 'processing'}>{value}</Tag> },
    { title: '发送时间', dataIndex: 'sent_at', width: 160, render: (value) => formatDateTime(value) },
    { title: '失败原因', dataIndex: 'error_message', render: (value) => value || '-' },
  ];

  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ marginBottom: 16 }}>通知提醒</h2>
      <Card size='small' style={{ marginBottom: 16 }}>
        <Space direction='vertical' style={{ width: '100%' }} size={12}>
          <Form form={queryForm} layout='inline'>
            <Form.Item name='keyword' label='关键词'>
              <Input placeholder='标题/摘要/正文' allowClear style={{ width: 220 }} />
            </Form.Item>
            <Form.Item name='type' label='类型'>
              <Select options={[{ label: '全部类型', value: '' }, ...TYPE_OPTIONS]} style={{ width: 140 }} allowClear />
            </Form.Item>
            <Form.Item name='status' label='状态'>
              <Select options={STATUS_OPTIONS} style={{ width: 150 }} allowClear />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type='primary' onClick={() => { setPage(1); loadList(); }}>查询</Button>
                <Button onClick={() => { queryForm.resetFields(); setPage(1); loadList(); }}>重置</Button>
                {canManage && <Button onClick={openCreate}>新增通知</Button>}
              </Space>
            </Form.Item>
          </Form>

          <div style={{ fontSize: 12, color: '#64748b' }}>
            当前账号微信绑定：{bindStatus.bound ? `已绑定（${bindStatus.openidMasked || '已绑定'}）` : '未绑定'}
            {bindStatus.bindAt ? `，绑定时间 ${formatDateTime(bindStatus.bindAt)}` : ''}
          </div>
        </Space>
      </Card>

      <Card size='small'>
        <Table
          rowKey='id'
          loading={listLoading}
          dataSource={records}
          columns={columns}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            },
          }}
        />
      </Card>

      <Modal
        open={modalOpen}
        title={editingRecord ? '编辑通知提醒' : '新增通知提醒'}
        width={900}
        onCancel={() => { setModalOpen(false); resetEditor(); }}
        onOk={submitEditor}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={editForm} layout='vertical' initialValues={DEFAULT_FORM_VALUES}>
          <Form.Item name='type' label='类型' rules={[{ required: true, message: '请选择类型' }]}> 
            <Radio.Group options={TYPE_OPTIONS} optionType='button' />
          </Form.Item>
          <Form.Item name='title' label='标题' rules={[{ required: true, message: '请输入标题' }]}> 
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item name='summary' label='摘要'>
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
          <Form.Item name='content' label='正文'>
            <Input.TextArea rows={6} />
          </Form.Item>
          <Space align='start' style={{ display: 'flex' }} size={16}>
            <Form.Item name='sendMode' label='发送方式' rules={[{ required: true, message: '请选择发送方式' }]} style={{ minWidth: 220 }}>
              <Radio.Group>
                <Radio.Button value='immediate'>立即发送</Radio.Button>
                <Radio.Button value='scheduled'>定时发送</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              name='scheduledAt'
              label='计划发送时间'
              rules={sendMode === 'scheduled' ? [{ required: true, message: '请选择计划发送时间' }] : []}
            >
              <DatePicker showTime style={{ width: 220 }} />
            </Form.Item>
          </Space>
          <Form.Item name='sendChannels' label='发送渠道' rules={[{ required: true, message: '至少选择一个渠道' }]}>
            <Select mode='multiple' options={CHANNEL_OPTIONS} />
          </Form.Item>
          <Form.Item name='userIds' label='指定员工'>
            <Select
              mode='multiple'
              showSearch
              filterOption={false}
              onSearch={searchUsers}
              options={userOptions}
              notFoundContent={userSearchLoading ? '搜索中...' : '输入姓名或部门搜索'}
            />
          </Form.Item>
          <Form.Item name='roleNames' label='按角色发送'>
            <Select mode='multiple' options={recipientOptions.roles} />
          </Form.Item>
          <Form.Item name='departments' label='按部门发送'>
            <Select mode='multiple' options={recipientOptions.departments} />
          </Form.Item>
          {sendChannels.includes('wechat_mini_program') && (
            <>
              <Form.Item name='miniProgramPage' label='小程序页面路径' rules={[{ required: true, message: '请输入小程序页面路径' }]}> 
                <Input placeholder='例如：pages/hr-notification/detail' />
              </Form.Item>
              <Form.Item name='miniProgramQuery' label='小程序附加参数'>
                <Input placeholder='例如：source=hr&scene=notice' />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <Modal
        open={deliveryOpen}
        title='投递记录'
        width={900}
        footer={null}
        onCancel={() => setDeliveryOpen(false)}
      >
        <Table
          rowKey='id'
          size='small'
          loading={deliveryLoading}
          dataSource={deliveryRows}
          columns={deliveryColumns}
          pagination={{ pageSize: 8 }}
        />
      </Modal>
    </div>
  );
}